import * as XLSX from 'xlsx';

export const GST_OPTIONS = [0, 5, 12, 18, 28, 40];
export const STATUS_OPTIONS = ['PUBLISHED', 'DRAFT', 'OUT OF STOCK'];

export const DEFAULT_ROW_VALUES = {
  gst: 18,
  stock: '',
  moq: 1,
  status: 'PUBLISHED',
  hsn: '',
  // Use only facts from the supplier; description, image and datasheet are our own.
  ownContent: true,
};

let rowCounter = 0;
const nextRowId = () => {
  rowCounter += 1;
  return `ql-${Date.now()}-${rowCounter}`;
};

// ---------------------------------------------------------------- input ----

/**
 * Parses the pasted text. Each line is: part number [, stock [, price]].
 * Tabs (pasted from Excel), commas and semicolons are accepted as separators.
 */
export const parsePartInput = (text) => {
  const seen = new Set();
  const entries = [];

  String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line, index) => {
      const cells = line.split(/\t|;|,/).map((c) => c.trim());
      const partNumber = cells[0];
      if (!partNumber) return;
      // Skip a header row copied from a spreadsheet.
      if (index === 0 && /^(part|mpn|product)/i.test(partNumber)) return;

      const key = partNumber.toUpperCase();
      if (seen.has(key)) return;
      seen.add(key);

      entries.push({
        partNumber,
        stock: cells[1] || '',
        price: cells[2] || '',
      });
    });

  return entries;
};

// Column headers understood when importing a file (compared without case,
// spaces or punctuation). Covers the "Quick Create Format" sheet
// (Product Overview / Categories / AdditionalResources / Assets) as well as
// simple "Part Number, Stock, Price" sheets.
const IMPORT_COLUMNS = {
  partNumber: ['partnumber', 'partno', 'part', 'mpn', 'productnumber', 'productno', 'manufacturerpartnumber', 'mfrpartnumber', 'mfrpartno', 'itemcode', 'pn'],
  stock: ['stock', 'qty', 'quantity', 'qtyavailable', 'availablestock', 'available'],
  price: ['price', 'unitprice', 'rate', 'cost'],
  description: ['description', 'desc'],
  manufacturer: ['manufacturer', 'make', 'brand', 'mfr'],
  datasheetUrl: ['datasheeturl', 'datasheet'],
  leadTime: ['manufactureleadtime', 'leadtime'],
  categories: ['categories', 'category'],
  hsn: ['hsncode', 'hsn'],
  spq: ['standardpackagingquantity', 'standardpackqty', 'spq'],
  image: ['image', 'imageurl'],
  featuredImage: ['featuredimage'],
};

const headerKey = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Reads part numbers (and any details already filled in) from spreadsheet
 * rows (array of arrays, e.g. from XLSX). The header row is found by its
 * part-number column; without one, column A = part, B = stock, C = price.
 */
export const parseSpreadsheetRows = (aoa = []) => {
  const rows = (aoa || []).filter((r) => Array.isArray(r) && r.some((c) => String(c ?? '').trim()));
  if (!rows.length) return [];

  let headerIndex = -1;
  const columns = {};

  for (let i = 0; i < Math.min(rows.length, 5); i += 1) {
    const keys = rows[i].map(headerKey);
    if (!keys.some((k) => IMPORT_COLUMNS.partNumber.includes(k))) continue;
    headerIndex = i;
    Object.entries(IMPORT_COLUMNS).forEach(([field, names]) => {
      const index = keys.findIndex((k) => names.includes(k));
      if (index !== -1) columns[field] = index;
    });
    break;
  }

  if (headerIndex === -1) {
    Object.assign(columns, { partNumber: 0, stock: 1, price: 2 });
  }

  const cell = (r, field) => (columns[field] === undefined ? '' : String(r[columns[field]] ?? '').trim());

  const seen = new Set();
  const entries = [];
  rows.slice(headerIndex + 1).forEach((r) => {
    const partNumber = cell(r, 'partNumber');
    if (!partNumber || seen.has(partNumber.toUpperCase())) return;
    seen.add(partNumber.toUpperCase());

    const stock = cell(r, 'stock').replace(/,/g, '');
    const entry = {
      partNumber,
      stock: /^[0-9.]+$/.test(stock) ? stock : '',
      price: cell(r, 'price'),
    };
    // Details already typed in the file are kept; the lookup fills the rest.
    ['description', 'manufacturer', 'datasheetUrl', 'leadTime', 'categories', 'hsn', 'spq'].forEach((field) => {
      const value = cell(r, field);
      if (value) entry[field] = value;
    });
    const image = cell(r, 'image').split(',')[0].trim() || cell(r, 'featuredImage');
    if (image) entry.image = image;

    entries.push(entry);
  });
  return entries;
};

// Same layout as the "Quick Create Format" sheet: fill productNumber (the
// rest is optional) and upload it on the Quick List page.
export const QUICK_CREATE_COLUMNS = [
  ['', 'Serial No'],
  ['Product Overview', 'productNumber'],
  ['Product Overview', 'datasheetUrl'],
  ['Product Overview', 'description'],
  ['Product Overview', 'manufacturer'],
  ['Product Overview', 'manufactureLeadTime'],
  ['Categories', 'categories'],
  ['AdditionalResources', 'HSN Code'],
  ['AdditionalResources', 'Standard_Packaging_Quantity'],
  ['Assets', 'image'],
  ['Assets', 'featuredImage'],
];

export const downloadQuickListTemplate = () => {
  const sheet = XLSX.utils.aoa_to_sheet([
    QUICK_CREATE_COLUMNS.map(([group]) => group),
    QUICK_CREATE_COLUMNS.map(([, header]) => header),
  ]);
  sheet['!cols'] = QUICK_CREATE_COLUMNS.map(([, header]) => ({ wch: Math.max(header.length + 4, 14) }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Sheet1');
  XLSX.writeFile(workbook, 'Quick Create Format.xlsx');
};

/**
 * Finds our category for a name or path typed in a file, e.g.
 * "Semiconductors > Interface ICs > Analog Switches" or just "Analog Switches".
 */
export const matchCategoryText = (text, options = []) => {
  const value = String(text || '').trim();
  if (!value || !options.length) return null;
  const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

  const byLabel = options.find((o) => norm(o.label) === norm(value) || norm(o.names.join(' > ')) === norm(value));
  if (byLabel) return byLabel;

  const tryParts = (parts) => {
    if (!parts.length) return null;
    const leaf = norm(parts[parts.length - 1]);
    const candidates = options
      .filter((o) => norm(o.names[o.names.length - 1]) === leaf)
      .filter((o) => parts.slice(0, -1).every((p) => o.names.some((n) => norm(n) === norm(p))));
    return candidates.sort((a, b) => b.names.length - a.names.length)[0] || null;
  };

  return (
    tryParts(value.split(/\s*>\s*/).filter(Boolean)) ||
    tryParts(value.split(/\s*(?:>|\/|\||,)\s*/).filter(Boolean))
  );
};

export const readSpreadsheetFile = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  return parseSpreadsheetRows(XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' }));
};

export const createRow = (entry, defaults = DEFAULT_ROW_VALUES) => ({
  id: nextRowId(),
  include: true,
  lookupStatus: 'pending', // pending | found | notfound | error
  source: null,
  existingProductId: null,
  partNumber: entry.partNumber,
  productNumber: entry.partNumber,
  manufacturer: entry.manufacturer || '',
  description: entry.description || '',
  datasheetUrl: entry.datasheetUrl || '',
  image: entry.image || '',
  categoryText: entry.categories || '', // category typed in an uploaded file
  supplierCategory: '',
  supplierCategoryPath: [],
  supplierUrl: '',
  categoryId: '',
  categoryAuto: false,
  // Specs as editable rows: { id, supplierName, name, value, include }.
  specs: [],
  rohs: 'YES',
  leadTime: entry.leadTime || '',
  spq: entry.spq || '', // standard packaging quantity
  hsCodeHint: '', // supplier's 6-digit HS code, shown as a hint for the HSN field
  sku: '',
  stock: entry.stock !== '' && entry.stock !== undefined ? entry.stock : defaults.stock,
  prices: entry.price || '',
  moq: defaults.moq,
  gst: defaults.gst,
  hsn: entry.hsn || defaults.hsn,
  status: defaults.status,
});

/** Merges a backend lookup result into a row without losing values the user typed. */
// Supplier specs plus the facts read from its description (Mouser sends few specs).
export function supplierFactsOf(result) {
  return {
    ...(result?.attributes || {}),
    ...extractSpecsFromDescription(result?.description),
  };
}

export const isSupplierHostedUrl = (url) => {
  try {
    const host = new URL(String(url || '')).hostname.toLowerCase();
    return /(^|\.)(mouser|digikey)\.[a-z.]+$/.test(host);
  } catch (e) {
    return false;
  }
};

const findPackage = (row) => {
  const specs = row.specs || [];
  const hit =
    specs.find((s) => s.include && /^package/i.test(s.name || '') && s.value) ||
    specs.find((s) => /package/i.test(s.supplierName || '') && s.value);
  return hit ? String(hit.value).split(',')[0].trim() : '';
};

/**
 * Our own short description built from facts only, e.g.
 * "MCP6561UT-E/OT – Analog Comparators, SOT-23-5 package, by Microchip Technology".
 * Used whenever a row has no description of its own.
 */
export const buildOwnDescription = (row, categoryById) => {
  const partNumber = String(row.productNumber || row.partNumber || '').trim();
  const category = categoryById?.get?.(row.categoryId);
  const kind = category ? category.names[category.names.length - 1] : row.supplierCategory || '';
  const pkg = findPackage(row);
  const mfr = String(row.manufacturer || '').trim();
  const details = [kind, pkg && `${pkg} package`].filter(Boolean).join(', ');
  return [partNumber, details && `– ${details}`, mfr && `${details ? ', by' : 'by'} ${mfr}`]
    .filter(Boolean)
    .join(' ')
    .replace(' ,', ',');
};

export const applyLookupResult = (row, result, categoryOptions, attributeOptions = [], { ownContent = true } = {}) => {
  if (!result) return { ...row, lookupStatus: 'error' };

  const next = {
    ...row,
    existingProductId: result.existingProductId || null,
    // Updating an existing product overwrites it, so those rows start unticked.
    include: result.existingProductId ? false : row.include,
  };

  if (!result.found) {
    return { ...next, lookupStatus: 'notfound' };
  }

  const suggestion = suggestCategory(result.categoryPath, result.category, categoryOptions);
  // "Our own content": keep only facts from the supplier. Their description,
  // images, supplier-hosted datasheet links and stock lead time are not copied;
  // the description is generated from the facts and the image comes from our
  // own catalog (see buildOwnDescription and the page's category image lookup).
  const supplierDatasheet = ownContent && isSupplierHostedUrl(result.datasheetUrl) ? '' : result.datasheetUrl;

  return {
    ...next,
    lookupStatus: 'found',
    source: result.source,
    productNumber: result.productNumber || row.productNumber,
    manufacturer: row.manufacturer || result.manufacturer || '',
    description: row.description || (ownContent ? '' : result.description || ''),
    datasheetUrl: row.datasheetUrl || supplierDatasheet || '',
    image: row.image || (ownContent ? '' : result.images?.[0] || ''),
    supplierCategory: result.category || '',
    supplierCategoryPath: result.categoryPath || [],
    supplierUrl: result.supplierUrl || '',
    categoryId: row.categoryId || suggestion?.id || '',
    categoryAuto: !row.categoryId && Boolean(suggestion),
    // Only our own attribute names are used; supplier specs that match none of
    // them are dropped. Values already in the row are kept.
    specs: fillSpecsFromFacts(
      row.specs || [],
      supplierFactsOf(result),
      attributeOptions,
      categoryOptions.find((o) => o.id === (row.categoryId || suggestion?.id))
    ),
    // Supplier facts (name -> values), kept to fill attributes when the
    // category changes. Never shown on the website or exported.
    supplierFacts: supplierFactsOf(result),
    rohs: result.rohs || row.rohs,
    leadTime: row.leadTime || (ownContent ? '' : result.leadTime || ''),
    // Kept only to read facts from (never shown on the website or exported).
    supplierDescription: result.description || '',
    spq: row.spq || result.standardPackQty || '',
    hsCodeHint: result.hsCode || '',
  };
};

// ------------------------------------------------------------ categories ----

const toId = (value) => (value?._id || value)?.toString?.() || '';
const parentOf = (item) => toId(Array.isArray(item?.parentId) ? item.parentId[0] : item?.parentId);

/**
 * Flattens the category hierarchy from redux into selectable options.
 * Level 3 entries give "Category > Subcategory > Level 3"; Level 2 entries
 * are offered too because some products live directly on a subcategory.
 */
export const buildCategoryOptions = (hierarchy) => {
  const categories = hierarchy?.categories || [];
  const subcategories = hierarchy?.subcategories || [];
  const level3 = hierarchy?.level3categories || [];

  const catById = new Map(categories.map((c) => [toId(c), c]));
  const subById = new Map(subcategories.map((s) => [toId(s), s]));
  const options = [];

  // The attribute types a category expects: its own list, else its parent's.
  const attrsOf = (...chain) => {
    const withAttrs = chain.find((c) => Array.isArray(c?.atributes) && c.atributes.length > 0);
    return withAttrs
      ? [...new Set(withAttrs.atributes.map((a) => String(a?.type || a?.name || a || '').trim()).filter(Boolean))]
      : [];
  };

  subcategories.forEach((sub) => {
    const cat = catById.get(parentOf(sub));
    if (!cat?.name || !sub?.name) return;
    options.push({ id: toId(sub), names: [cat.name, sub.name], atributes: attrsOf(sub, cat) });
  });

  level3.forEach((l3) => {
    const sub = subById.get(parentOf(l3));
    const cat = sub && catById.get(parentOf(sub));
    if (!cat?.name || !sub?.name || !l3?.name) return;
    options.push({ id: toId(l3), names: [cat.name, sub.name, l3.name], atributes: attrsOf(l3, sub, cat) });
  });

  return options
    .map((o) => ({
      ...o,
      names: o.names.map((n) => String(n).trim()),
      label: o.names.map((n) => String(n).trim()).join(' > '),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const STOP_WORDS = new Set(['ic', 'ics', 'and', 'the', 'of', 'for', 'with', 'other', 'misc']);

const tokenize = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .map((t) => (t.length > 3 && t.endsWith('s') ? t.slice(0, -1) : t))
    .filter((t) => t && !STOP_WORDS.has(t));

const normalizeName = (value) => tokenize(value).join(' ');

const similarity = (a, b) => {
  const ta = new Set(tokenize(a));
  const tb = new Set(tokenize(b));
  if (!ta.size || !tb.size) return 0;
  let common = 0;
  ta.forEach((t) => {
    if (tb.has(t)) common += 1;
  });
  return common / new Set([...ta, ...tb]).size;
};

/**
 * Picks one of our own categories for a supplier category. Returns null when
 * nothing is close enough — the user then chooses manually.
 */
export const suggestCategory = (supplierPath = [], supplierLeaf = '', options = []) => {
  const path = (supplierPath && supplierPath.length ? supplierPath : [supplierLeaf]).filter(Boolean);
  if (!path.length || !options.length) return null;

  // 1. Exact name match, deepest supplier level first.
  for (let i = path.length - 1; i >= 0; i -= 1) {
    const wanted = normalizeName(path[i]);
    const exact = options
      .filter((o) => normalizeName(o.names[o.names.length - 1]) === wanted)
      .sort((a, b) => b.names.length - a.names.length)[0];
    if (exact) return exact;
  }

  // 2. Closest leaf name by shared words.
  const leaf = path[path.length - 1];
  let best = null;
  let bestScore = 0;
  options.forEach((o) => {
    const score = similarity(leaf, o.names[o.names.length - 1]) + 0.1 * similarity(path.join(' '), o.label);
    if (score > bestScore) {
      best = o;
      bestScore = score;
    }
  });

  return bestScore >= 0.5 ? best : null;
};

// ------------------------------------------------------------ attributes ----

// Website filters match attribute names exactly, so supplier spec names must
// be mapped onto our master attribute list (variant_name) before saving.
// Each group: supplier-style names -> our names to try, in order.
const SPEC_ALIASES = [
  [['package case', 'package', 'case package', 'supplier device package', 'package type', 'case'], ['Package', 'Package / Case', 'Package Type', 'Case']],
  [['mounting style', 'mounting type', 'mounting'], ['Mounting Type', 'Mounting Style', 'Mounting']],
  [['operating temperature', 'operating temperature range'], ['Operating Temperature', 'Operating Temperature Range']],
  [['supply voltage', 'operating supply voltage', 'voltage supply'], ['Supply Voltage', 'Voltage - Supply', 'Operating Voltage', 'Voltage']],
  [['number of channel', 'channel'], ['Number of Channels', 'Channels']],
  [['number of pin', 'pin count', 'number of terminal'], ['Number of Pins', 'Pin Count', 'Pins']],
  [['on resistance max', 'on state resistance max', 'on resistance'], ['On Resistance', 'On Resistance (Max)']],
  [['voltage rating', 'rated voltage', 'voltage rated', 'voltage dc'], ['Voltage Rating', 'Volatge Rating', 'Rated Voltage', 'Voltage - Rated', 'Voltage']],
  [['temperature coefficient', 'dielectric'], ['Temperature Coefficient', 'Temperature Coeffecient', 'Dielectric']],
  [['power rating', 'power'], ['Power Rating', 'Power(Watts)', 'Power-Max', 'Power']],
];

// Optimal string alignment distance (Levenshtein + swapped neighbours), so
// typos in our own attribute names ("Volatge Rating") still match.
const editDistance = (a, b) => {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j += 1) d[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  }
  return d[a.length][b.length];
};

const sameWordsWithTypos = (a, b) => {
  const ta = tokenize(a);
  const tb = tokenize(b);
  if (!ta.length || ta.length !== tb.length) return false;
  const remaining = [...tb];
  return ta.every((word) => {
    const allowed = word.length >= 6 ? 2 : word.length >= 4 ? 1 : 0;
    const index = remaining.findIndex((other) => editDistance(word, other) <= allowed);
    if (index === -1) return false;
    remaining.splice(index, 1);
    return true;
  });
};

let specCounter = 0;
export const newSpec = (patch = {}) => {
  specCounter += 1;
  return { id: `sp-${Date.now()}-${specCounter}`, supplierName: '', name: '', value: '', include: true, ...patch };
};

const tokenKey = (value) => [...new Set(tokenize(value))].sort().join(' ');

export const matchAttributeName = (supplierName, attributeOptions = [], used = new Set()) => {
  const free = attributeOptions.filter((o) => o && !used.has(o));
  if (!supplierName || !free.length) return '';
  const norm = normalizeName(supplierName);
  if (!norm) return '';

  const exact = free.find((o) => normalizeName(o) === norm);
  if (exact) return exact;

  const key = tokenKey(supplierName);
  const sameWords = free.find((o) => tokenKey(o) === key);
  if (sameWords) return sameWords;

  const withTypos = free.find((o) => sameWordsWithTypos(supplierName, o));
  if (withTypos) return withTypos;

  const group = SPEC_ALIASES.find(([names]) => names.some((n) => normalizeName(n) === norm));
  if (group) {
    for (const candidate of group[1]) {
      const hit = free.find((o) => normalizeName(o) === normalizeName(candidate) || sameWordsWithTypos(candidate, o));
      if (hit) return hit;
    }
  }

  let best = '';
  let bestScore = 0;
  free.forEach((o) => {
    const score = similarity(supplierName, o);
    if (score > bestScore) {
      best = o;
      bestScore = score;
    }
  });
  return bestScore >= 0.67 ? best : '';
};

// ------------------------------------------------- specs from description ----

const CASE_CODES = ['01005', '0201', '0402', '0603', '0805', '1008', '1206', '1210', '1806', '1808', '1812', '1825', '2010', '2220', '2225', '2512'];
const DIELECTRICS = ['C0G', 'NP0', 'X5R', 'X5S', 'X6S', 'X6T', 'X7R', 'X7S', 'X7T', 'X8L', 'X8R', 'Y5V', 'Z5U', 'U2J'];

const trimNumber = (n) => String(+n.toFixed(3));

// 3300pF -> 3.3nF, 0.01uF -> 10nF, 1uF -> 1µF (the format used on our website).
const formatCapacitance = (value, unit) => {
  const factor = { p: 1, n: 1e3, u: 1e6, µ: 1e6, μ: 1e6, m: 1e9 }[unit[0].toLowerCase()] || 1;
  const pF = parseFloat(value) * factor;
  if (!Number.isFinite(pF) || pF <= 0) return '';
  if (pF < 1000) return `${trimNumber(pF)}pF`;
  if (pF < 1e6) return `${trimNumber(pF / 1e3)}nF`;
  return `${trimNumber(pF / 1e6)}µF`;
};

const formatResistance = (value, prefix) => {
  const ohms = parseFloat(value) * ({ k: 1e3, K: 1e3, M: 1e6, m: 1e-3 }[prefix] || 1);
  if (!Number.isFinite(ohms)) return '';
  if (ohms >= 1e6) return `${trimNumber(ohms / 1e6)}MΩ`;
  if (ohms >= 1e3) return `${trimNumber(ohms / 1e3)}kΩ`;
  return `${trimNumber(ohms)}Ω`;
};

/**
 * Reads the facts printed in a supplier's description of a passive part
 * (capacitors, resistors, inductors), e.g.
 * "MLCC - SMD/SMT 16V 3300pF X7R 0603 10%" ->
 * { Capacitance: 3.3nF, Voltage Rating: 16V, Temperature Coefficient: X7R,
 *   Package / Case: 0603, Series: 0603, Tolerance: ±10%, Type: SMD }.
 * Descriptions that mention another part (replacement notices) are ignored.
 */
export const extractSpecsFromDescription = (description) => {
  const text = String(description || '');
  const specs = {};
  if (!/capacitor|mlcc|resistor|inductor|ferrite|choke/i.test(text)) return specs;
  if (/recommended alt|replacement|\b\d{2,3}-[A-Z0-9]{5,}/i.test(text)) return specs;

  const set = (name, value) => {
    if (value && !specs[name]) specs[name] = [value];
  };

  const cap = text.match(/(\d+(?:\.\d+)?)\s*(pF|nF|uF|µF|μF|mF)\b/i);
  if (cap) set('Capacitance', formatCapacitance(cap[1], cap[2]));

  const res = text.match(/(\d+(?:\.\d+)?)\s*([kKM]?)\s*(?:Ω|Ohms?\b)/);
  if (res) set('Resistance', formatResistance(res[1], res[2]));

  const ind = text.match(/(\d+(?:\.\d+)?)\s*(nH|uH|µH|μH|mH)\b/i);
  if (ind) set('Inductance', `${ind[1]}${ind[2].replace(/^u/i, 'µ')}`);

  const volt = text.match(/(\d+(?:\.\d+)?)\s*(k?V)(?:DC|AC)?\b/);
  if (volt) set('Voltage Rating', `${volt[1]}${volt[2]}`);

  const tol = text.match(/(?:±|\+\/-)?\s*(\d+(?:\.\d+)?)\s*%/);
  if (tol) set('Tolerance', `±${tol[1]}%`);

  const power = text.match(/(\d+\/\d+|\d+(?:\.\d+)?)\s*W\b/);
  if (power) set('Power Rating', `${power[1]}W`);

  const dielectric = DIELECTRICS.find((d) => new RegExp(`\\b${d}\\b`).test(text));
  if (dielectric) set('Temperature Coefficient', dielectric);

  const caseCode = CASE_CODES.find((c) => new RegExp(`\\b${c}\\b`).test(text));
  if (caseCode) {
    set('Package / Case', caseCode);
    // Our chip capacitors/resistors use the case size as their "Series".
    set('Series', caseCode);
  }

  if (/\bSMD\b|\bSMT\b|surface mount/i.test(text)) set('Type', 'SMD');
  else if (/through hole|radial|axial/i.test(text)) set('Type', 'Through Hole');

  return specs;
};

/**
 * Attribute names to map onto: the selected category's own list first (so
 * values land on the names its filters use), then the full master list.
 */
export const preferCategoryAttributes = (attributeOptions = [], categoryOption) => {
  const preferred = (categoryOption?.atributes || []).filter(Boolean);
  const lower = new Set(preferred.map((a) => a.toLowerCase()));
  return [...preferred, ...attributeOptions.filter((a) => !lower.has(String(a).toLowerCase()))];
};

/**
 * Turns supplier specs ({ name: [values] }) into editable spec rows. Specs
 * that match one of our attributes are included; the rest stay visible but
 * unticked so they do not create new website filters by accident.
 */
export const mapSupplierSpecs = (supplierAttributes = {}, attributeOptions = []) => {
  const used = new Set();
  return Object.entries(supplierAttributes || {}).map(([supplierName, values]) => {
    const value = (Array.isArray(values) ? values : [values])
      .map((v) => String(v ?? '').trim())
      .filter(Boolean)
      .join(', ');
    const name = matchAttributeName(supplierName, attributeOptions, used);
    if (name) used.add(name);
    return newSpec({ supplierName, name, value, include: Boolean(name) });
  });
};

/**
 * Adds attribute values read from the supplier's description to a row's specs
 * without touching values that are already filled. Used when the category is
 * picked later and from the "Fill from supplier facts" button.
 */
export const fillSpecsFromFacts = (specs = [], factsOrDescription, attributeOptions = [], categoryOption) => {
  const facts =
    typeof factsOrDescription === 'string' ? extractSpecsFromDescription(factsOrDescription) : factsOrDescription || {};
  if (!Object.keys(facts).length) return specs;

  const names = preferCategoryAttributes(attributeOptions, categoryOption);
  const filled = new Set(specs.filter((s) => s.name && String(s.value || '').trim()).map((s) => s.name.toLowerCase()));
  // Names that already have a value are not offered again.
  const used = new Set(names.filter((n) => filled.has(n.toLowerCase())));
  const next = [...specs];

  Object.entries(facts).forEach(([supplierName, values]) => {
    const name = matchAttributeName(supplierName, names, used);
    if (!name) return;
    used.add(name);
    const value = values.join(', ');
    const emptyIndex = next.findIndex((s) => s.name.toLowerCase() === name.toLowerCase());
    if (emptyIndex !== -1) next[emptyIndex] = { ...next[emptyIndex], value, include: true };
    else next.push(newSpec({ supplierName, name, value, include: true }));
  });

  return next;
};

/** Maps specs that are still unmapped (e.g. the attribute list loaded late). */
export const remapUnmappedSpecs = (specs = [], attributeOptions = []) => {
  const used = new Set(specs.filter((s) => s.name).map((s) => s.name));
  let changed = false;
  const next = specs.map((s) => {
    if (s.name || !s.supplierName) return s;
    const name = matchAttributeName(s.supplierName, attributeOptions, used);
    if (!name) return s;
    used.add(name);
    changed = true;
    return { ...s, name, include: true };
  });
  return changed ? next : specs;
};

export const specsToAttributes = (specs = []) => {
  const attributes = {};
  specs.forEach((s) => {
    const name = String(s.name || '').trim();
    const value = String(s.value || '').trim();
    if (!s.include || !name || !value) return;
    if (!attributes[name]) attributes[name] = [];
    if (!attributes[name].includes(value)) attributes[name].push(value);
  });
  return attributes;
};

// Rows saved by an older version of this page kept a plain attributes object.
export const getRowAttributes = (row) =>
  Array.isArray(row.specs) ? specsToAttributes(row.specs) : { ...(row.attributes || {}) };

/** Attributes the selected category expects that this row has no value for. */
export const getMissingCategoryAttributes = (row, categoryById) => {
  const expected = categoryById?.get(row.categoryId)?.atributes || [];
  if (!expected.length) return [];
  const filled = new Set(Object.keys(getRowAttributes(row)).map((k) => k.toLowerCase()));
  return expected.filter((a) => !filled.has(a.toLowerCase()) && a.toLowerCase() !== 'manufacturer');
};

// ---------------------------------------------------------------- prices ----

const toNumber = (value) => {
  const cleaned = String(value ?? '').replace(/[^0-9.-]/g, '');
  // "abc" or "" must not silently become 0 (a ₹0 price).
  if (!cleaned) return NaN;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : NaN;
};

/**
 * "12.5"               -> one break at the MOQ
 * "1:12.5, 100:10.2"   -> one break per "qty:price" pair
 */
export const parsePriceBreaks = (value, moq = 1) => {
  const text = String(value ?? '').trim();
  if (!text) return [];

  const minQty = toNumber(moq) > 0 ? toNumber(moq) : 1;

  if (!text.includes(':')) {
    const price = toNumber(text);
    return Number.isFinite(price) ? [{ breakQty: minQty, unitPrice: price }] : [];
  }

  return text
    .split(/[,;|]/)
    .map((pair) => pair.split(':').map((p) => p.trim()))
    .map(([qty, price]) => ({ breakQty: toNumber(qty), unitPrice: toNumber(price) }))
    .filter((b) => Number.isFinite(b.breakQty) && b.breakQty > 0 && Number.isFinite(b.unitPrice))
    .sort((a, b) => a.breakQty - b.breakQty);
};

// Same formula as PricingCard: price including GST, 3 decimals.
export const finalPriceWithGst = (unitPrice, gst) =>
  +(Number(unitPrice) + (Number(unitPrice) * Number(gst || 0)) / 100).toFixed(3);

// ------------------------------------------------------------ validation ----

export const getRowIssues = (row) => {
  const issues = [];
  if (!String(row.productNumber || '').trim()) issues.push('Part number missing');
  if (!row.categoryId) issues.push('Category not selected');
  if (!String(row.manufacturer || '').trim()) issues.push('Manufacturer missing');
  if (row.prices && parsePriceBreaks(row.prices, row.moq).length === 0) issues.push('Price format invalid');
  return issues;
};

// --------------------------------------------------- bulk upload payload ----

/**
 * Converts Quick List rows into the exact object shape produced by
 * excelProductParser, so the existing Bulk Upload review page and
 * /admin/bulkupload/save-product work without any change.
 */
export const buildBulkProducts = (rows, categoryOptions = []) => {
  const optionById = new Map(categoryOptions.map((o) => [o.id, o]));

  return rows.map((row, index) => {
    const productNumber = String(row.productNumber || row.partNumber || '').trim();
    const manufacturer = String(row.manufacturer || '').trim();
    const category = optionById.get(row.categoryId);
    const gst = Number(row.gst);

    const attributes = getRowAttributes(row);
    if (manufacturer && !attributes.Manufacturer) attributes.Manufacturer = [manufacturer];

    const inventory = {
      qtyAvailable: Number.isFinite(toNumber(row.stock)) ? toNumber(row.stock) : 0,
      minimumMultiplier: toNumber(row.moq) > 0 ? toNumber(row.moq) : 1,
    };
    if (String(row.sku || '').trim()) inventory.sku = String(row.sku).trim();

    const hsn = String(row.hsn || '').trim();
    const spq = String(row.spq || '').trim();
    const additionalResources = {};
    if (hsn) additionalResources['HSN Code'] = [hsn];
    if (spq) additionalResources.Standard_Packaging_Quantity = [spq];

    return {
      serialNo: index + 1,
      'Serial No': index + 1,
      productOverview: {
        ElectrowizCode: productNumber ? [productNumber] : [],
        productNumber: productNumber ? [productNumber] : [],
        datasheetUrl: row.datasheetUrl ? [String(row.datasheetUrl).trim()] : [],
        description: [String(row.description || '').trim() || buildOwnDescription(row, optionById)].filter(Boolean),
        manufacturer: manufacturer ? [manufacturer] : [],
        manufactureLeadTime: row.leadTime ? [String(row.leadTime).trim()] : [],
      },
      attributes,
      categories: category ? category.names : ['General'],
      alternateProducts: [],
      RoHS: [row.rohs === 'NO' ? 'NO' : 'YES'],
      additionalResources,
      assets: {
        image: row.image ? [row.image] : [],
        video: [],
        featuredImage: row.image ? [row.image] : [],
      },
      inventory,
      quantityTable: parsePriceBreaks(row.prices, row.moq).map((b) => ({
        breakQty: b.breakQty,
        unitPrice: b.unitPrice,
        finalPrice: finalPriceWithGst(b.unitPrice, gst),
      })),
      gst: Number.isFinite(gst) ? gst : 18,
      status: row.status || 'PUBLISHED',
    };
  });
};

// ----------------------------------------------------------- Excel export ----

/**
 * Builds a sheet in the grouped-header layout that excelProductParser reads
 * (row 1 = group names, row 2 = field names). Extra price breaks go on extra
 * rows that repeat only the serial number, which the parser merges back.
 */
export const buildQuickListSheetData = (rows, categoryOptions = []) =>
  buildProductSheetData(buildBulkProducts(rows, categoryOptions));

const asList = (value) => (Array.isArray(value) ? value : value === undefined || value === null || value === '' ? [] : [value]);

/**
 * Same sheet for products in the bulk-upload shape (from Quick List rows or
 * from the catalog via catalogProductToBulkShape).
 */
export const buildProductSheetData = (products) => {
  const attributeKeys = [];
  const extraResourceKeys = [];
  products.forEach((p) => {
    Object.keys(p.attributes || {}).forEach((k) => {
      if (!attributeKeys.includes(k)) attributeKeys.push(k);
    });
    Object.keys(p.additionalResources || {}).forEach((k) => {
      if (!['HSN Code', 'Standard_Packaging_Quantity'].includes(k) && !extraResourceKeys.includes(k)) extraResourceKeys.push(k);
    });
  });

  // The first 11 columns follow the "Quick Create Format" sheet exactly; stock,
  // price, GST, status and attributes follow so the file is upload-ready.
  const quickCreateValues = {
    'Serial No': (p) => p.serialNo,
    productNumber: (p) => p.productOverview.productNumber[0],
    datasheetUrl: (p) => p.productOverview.datasheetUrl[0],
    description: (p) => p.productOverview.description[0],
    manufacturer: (p) => p.productOverview.manufacturer[0],
    manufactureLeadTime: (p) => p.productOverview.manufactureLeadTime[0],
    categories: (p) => (p.categories || []).join(' > '),
    'HSN Code': (p) => asList(p.additionalResources?.['HSN Code'])[0],
    Standard_Packaging_Quantity: (p) => asList(p.additionalResources?.Standard_Packaging_Quantity)[0],
    // Several images are written comma-separated; the parser splits them again.
    image: (p) => asList(p.assets?.image).join(', '),
    featuredImage: (p) => asList(p.assets?.featuredImage)[0],
  };

  const columns = [
    ...QUICK_CREATE_COLUMNS.map(([group, header]) => [group, header, quickCreateValues[header]]),
    ['Inventory', 'sku', (p) => p.inventory.sku],
    ['Inventory', 'qtyAvailable', (p) => p.inventory.qtyAvailable],
    ['Inventory', 'minimumMultiplier', (p) => p.inventory.minimumMultiplier],
    ['Quantity Table', 'breakQty', (p, q) => q?.breakQty],
    ['Quantity Table', 'unitPrice', (p, q) => q?.unitPrice],
    ['Quantity Table', 'finalPrice', (p, q) => q?.finalPrice],
    ['GST', 'gst', (p) => p.gst],
    ['Status', 'status', (p) => p.status],
    ['RoHS', 'RoHS', (p) => asList(p.RoHS)[0]],
    ...extraResourceKeys.map((key) => ['AdditionalResources', key, (p) => asList(p.additionalResources?.[key]).join(', ')]),
    ...attributeKeys.map((key) => ['Attributes', key, (p) => asList(p.attributes?.[key]).join(', ')]),
  ];

  // Group names are repeated in every cell, as in the Quick Create Format sheet.
  const groupRow = columns.map(([group]) => group);
  const headerRow = columns.map(([, header]) => header);

  const dataRows = [];
  products.forEach((p) => {
    const [firstBreak, ...moreBreaks] = p.quantityTable || [];
    dataRows.push(columns.map(([, , get]) => valueOrEmpty(get(p, firstBreak))));
    moreBreaks.forEach((q) => {
      dataRows.push(
        columns.map(([group, header, get]) =>
          header === 'Serial No' || group === 'Quantity Table' ? valueOrEmpty(get(p, q)) : ''
        )
      );
    });
  });

  return { aoa: [groupRow, headerRow, ...dataRows], merges: [] };
};

const valueOrEmpty = (value) => (value === undefined || value === null ? '' : value);

export const writeProductWorkbook = ({ aoa, merges = [] }, fileName) => {
  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  sheet['!merges'] = merges;
  sheet['!cols'] = aoa[1].map((h) => ({ wch: Math.min(Math.max(String(h).length + 4, 12), 40) }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Products');
  XLSX.writeFile(workbook, fileName);
};

export const downloadQuickListExcel = (rows, categoryOptions, fileName) => {
  const date = new Date().toISOString().slice(0, 10);
  writeProductWorkbook(buildQuickListSheetData(rows, categoryOptions), fileName || `quick-list-${date}.xlsx`);
};

// ------------------------------------------------------- catalog export ----

const idOf = (value) => String(value?._id || value || '');

/**
 * Orders a product's category documents (as returned by the product list API:
 * { _id, name, isCategory, parentId }) into "Category > Subcategory > Level 3".
 */
const categoryPathOf = (categories = []) => {
  const docs = categories.filter((c) => c && typeof c === 'object' && c.name);
  if (!docs.length) return categories.filter((c) => typeof c === 'string');
  const byId = new Map(docs.map((c) => [idOf(c), c]));
  const parentOf = (c) => idOf(Array.isArray(c.parentId) ? c.parentId[0] : c.parentId);
  // Start from the deepest one (no other category points to it) and walk up.
  const isParent = new Set(docs.map(parentOf).filter(Boolean));
  const leaf = docs.find((c) => !isParent.has(idOf(c))) || docs[docs.length - 1];
  const path = [];
  const seen = new Set();
  let current = leaf;
  while (current && !seen.has(idOf(current))) {
    seen.add(idOf(current));
    path.unshift(String(current.name).trim());
    current = byId.get(parentOf(current));
  }
  return path;
};

/** A product from the catalog (product list API) in the bulk-upload shape. */
export const catalogProductToBulkShape = (product, index = 0) => {
  const overview = product?.productOverview || {};
  const manufacturers = asList(overview.manufacturer)
    .map((m) => (typeof m === 'object' ? m?.name : m))
    .filter(Boolean)
    .map(String);
  return {
    serialNo: index + 1,
    'Serial No': index + 1,
    productOverview: {
      productNumber: asList(overview.productNumber).map(String),
      datasheetUrl: asList(overview.datasheetUrl).map(String),
      description: asList(overview.description).map(String),
      manufacturer: manufacturers,
      manufactureLeadTime: asList(overview.manufactureLeadTime).map(String),
    },
    attributes: product?.attributes && typeof product.attributes === 'object' ? product.attributes : {},
    categories: categoryPathOf(product?.categories || []),
    RoHS: asList(product?.RoHS),
    additionalResources: product?.additionalResources && typeof product.additionalResources === 'object' ? product.additionalResources : {},
    assets: {
      image: asList(product?.assets?.image),
      featuredImage: asList(product?.assets?.featuredImage),
    },
    inventory: {
      sku: product?.inventory?.sku,
      qtyAvailable: product?.inventory?.qtyAvailable ?? 0,
      minimumMultiplier: product?.inventory?.minimumMultiplier ?? 1,
    },
    quantityTable: (product?.quantityTable || []).map((q) => ({
      breakQty: q?.breakQty,
      unitPrice: q?.unitPrice,
      finalPrice: q?.finalPrice,
    })),
    gst: product?.gst,
    status: product?.status,
  };
};

export const downloadCatalogProductsExcel = (products, fileName) => {
  writeProductWorkbook(buildProductSheetData(products.map(catalogProductToBulkShape)), fileName);
};
